/**
 * "The first screen's work is done" (docs/ux/plan.md, B8): API requests and screen chunks in
 * flight are counted, and background downloads (the web fonts, idle prefetches) wait until
 * none has been in flight for a moment. On a slow connection they would otherwise share the
 * bandwidth, and HTTP/1.1's six connections, with the requests the screen is waiting for.
 *
 *   track(promise)        count work until it settles
 *   whenQuiet(work)       run once nothing has been in flight for QUIET_MS (at most after maxWaitMs)
 */
export const QUIET_MS = 150;

let busy = 0;
let waiters: Array<() => void> = [];
let timer: ReturnType<typeof setTimeout> | null = null;

function check(): void {
  if (busy > 0 || waiters.length === 0) return;
  if (timer) clearTimeout(timer);
  timer = setTimeout(() => {
    timer = null;
    if (busy > 0) return;
    const ready = waiters;
    waiters = [];
    for (const run of ready) run();
  }, QUIET_MS);
}

/** Count `work` as in flight until it settles; returns it unchanged */
export function track<T>(work: Promise<T>): Promise<T> {
  busy++;
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  const settled = () => {
    busy--;
    check();
  };
  work.then(settled, settled);
  return work;
}

/** Run `work` once, when nothing tracked has been in flight for QUIET_MS (or after maxWaitMs) */
export function whenQuiet(work: () => void, maxWaitMs = 6000): () => void {
  let done = false;
  const run = () => {
    if (done) return;
    done = true;
    clearTimeout(fallback);
    work();
  };
  const fallback = setTimeout(run, maxWaitMs);
  waiters.push(run);
  // After the current task, so work started in the same tick is counted first
  setTimeout(check, 0);
  return () => {
    done = true;
    clearTimeout(fallback);
    waiters = waiters.filter((w) => w !== run);
  };
}
